import { useCallback, useEffect, useRef, useState } from 'react';
import { WorkoutDeliveryManagementService } from '../services';
import type { PaginationInfo } from '../types';
import type {
  WorkoutDeliveryFilters,
  WorkoutDeliveryRow,
} from '../types/workoutDeliveryManagement';

const SEARCH_DEBOUNCE_MS = 400;

const EMPTY_PAGINATION: PaginationInfo = {
  page: 1,
  pageSize: 10,
  totalRecords: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

export interface UseWorkoutDeliveryManagementReturn {
  rows: WorkoutDeliveryRow[];
  pagination: PaginationInfo;
  loading: boolean;
  error: string | null;
  refresh: (options?: { silent?: boolean }) => void;
  removeRow: (studentId: number) => void;
  patchRow: (studentId: number, data: Partial<WorkoutDeliveryRow>) => void;
}

export function useWorkoutDeliveryManagement(
  page: number = 1,
  pageSize: number = 10,
  filters: WorkoutDeliveryFilters = {},
): UseWorkoutDeliveryManagementReturn {
  const [rows, setRows] = useState<WorkoutDeliveryRow[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reqId = useRef(0);
  const silentReqId = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevSearchName = useRef(filters.searchName);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const fetchPage = useCallback(async (
    requestedPage: number,
    requestedPageSize: number,
    serverFilters: WorkoutDeliveryFilters,
    options?: { silent?: boolean },
  ) => {
    const silent = Boolean(options?.silent);

    if (silent) {
      const snapshotReqId = reqId.current;
      const currentSilentReq = ++silentReqId.current;

      try {
        const response = await WorkoutDeliveryManagementService.listPaginated(
          requestedPage,
          requestedPageSize,
          serverFilters,
        );

        if (currentSilentReq !== silentReqId.current) return;
        if (snapshotReqId !== reqId.current) return;

        setRows(response.data);
        setPagination(response.pagination);
      } catch {
        // Silent refresh failures should not interrupt the screen.
      }

      return;
    }

    const currentReq = ++reqId.current;
    silentReqId.current += 1;

    try {
      setLoading(true);
      setError(null);

      const response = await WorkoutDeliveryManagementService.listPaginated(
        requestedPage,
        requestedPageSize,
        serverFilters,
      );

      if (currentReq !== reqId.current) return;

      setRows(response.data);
      setPagination(response.pagination);
    } catch (err: unknown) {
      if (currentReq !== reqId.current) return;
      const message = err instanceof Error ? err.message : 'Erro ao carregar entregas de treinos';
      setError(message);
    } finally {
      if (currentReq === reqId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const currentFilters = filtersRef.current;
    const onlySearchChanged = currentFilters.searchName !== prevSearchName.current;
    prevSearchName.current = currentFilters.searchName;

    if (onlySearchChanged) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchPage(page, pageSize, currentFilters);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      clearTimeout(debounceTimer.current);
      fetchPage(page, pageSize, currentFilters);
    }

    return () => clearTimeout(debounceTimer.current);
  }, [filters.searchName, fetchPage, page, pageSize]);

  const refresh = useCallback((options?: { silent?: boolean }) => {
    fetchPage(page, pageSize, filters, options);
  }, [fetchPage, filters, page, pageSize]);

  const removeRow = useCallback((studentId: number) => {
    setRows((prev) => prev.filter((row) => row.student_id !== studentId));
    setPagination((prev) => {
      const totalRecords = Math.max(0, prev.totalRecords - 1);
      const totalPages = totalRecords > 0 ? Math.ceil(totalRecords / prev.pageSize) : 0;
      return {
        ...prev,
        totalRecords,
        totalPages,
        hasNextPage: totalPages > 0 && prev.page < totalPages,
        hasPreviousPage: prev.page > 1 && totalPages > 0,
      };
    });
  }, []);

  const patchRow = useCallback((studentId: number, data: Partial<WorkoutDeliveryRow>) => {
    setRows((prev) => prev.map((row) => (
      row.student_id === studentId ? { ...row, ...data } : row
    )));
  }, []);

  return {
    rows,
    pagination,
    loading,
    error,
    refresh,
    removeRow,
    patchRow,
  };
}
